<template>
  <div>
    <p
      v-if="error"
      class="text-danger"
      >{{ errorMsg }}</p
    >

    <b-card
      class="company-location-card bg-gray-50 shadow-sm d-flex flex-row"
      no-body
    >
      <b-card-body>
        <b-card-title>
          <div class="d-flex justify-content-between align-items-baseline">
            <div class="d-flex align-items-center">
              {{ location.name }}
              <b-badge
                v-if="location.is_primary_location"
                variant="soft-primary"
                class="ml-3 text-uppercase font-size-80"
                >Primary</b-badge
              >
            </div>
            <div>
              <b-button-group>
                <b-button
                  v-if="!location.is_primary_location"
                  class="text-uppercase font-size-80"
                  variant="subtle-primary"
                  @click="setPrimaryLocation(location)"
                  :disabled="isDisabled"
                  >Set Primary</b-button
                >
                <b-button
                  class="text-uppercase font-size-80"
                  variant="subtle-info"
                  @click="handleLocationEdit(location)"
                  :disabled="isDisabled"
                  >Edit</b-button
                >
                <b-button
                  v-if="$currentUserInfo.is_paid_seat || $currentUserInfo.is_external_admin"
                  class="text-uppercase font-size-80"
                  variant="subtle-danger"
                  @click="handleLocationDelete(location)"
                  :disabled="isDisabled"
                  >Delete</b-button
                >
              </b-button-group>
            </div>
          </div>
        </b-card-title>
        <b-card-sub-title
          v-if="location.formatted_city_state_country"
          class="d-flex align-items-center"
        >
          <b-icon icon="geo-alt-fill"></b-icon>
          <span class="ml-2 mr-3">{{ location.formatted_city_state_country }}</span>
          <!-- <img :src="location.country_flag_url"> -->
          <country-flag
            :country="location.country"
            size="md"
          ></country-flag>
        </b-card-sub-title>

        <b-card-text class="">
          <div class="d-flex flex-wrap justify-content-between">
            <div
              v-if="showAddressBlock"
              class="pr-3 mt-4"
            >
              <h6 class="hot-edit-label">Address</h6>
              <p
                v-if="location.user_addr1"
                class="mb-0"
              >
                {{ location.user_addr1 }}{{ location.user_subpremise ? ', ' + location.user_subpremise : '' }}
              </p>
              <p
                v-if="location.user_addr2"
                class="mb-0"
              >
                {{ location.user_addr2 }}
              </p>
              <p
                v-if="location.formatted_city_state || location.user_postcode || location.api_postcode"
                class="mb-0"
              >
                <span class="mr-2">{{ location.formatted_city_state }}</span> {{ location.user_postcode || location.api_postcode }}
              </p>
              <p
                v-if="location.country_name"
                class="mb-0"
              >
                {{ location.country_name }}
              </p>
            </div>

            <div
              v-if="showLocalTimeBlock"
              class="pr-3 mt-4"
            >
              <h6 class="hot-edit-label">Current Local Time</h6>
              <time-zone-clock
                v-if="location.time_zone"
                :iana-zone="location.time_zone"
                class=""
              ></time-zone-clock>
            </div>

            <div
              v-if="showAdditionalInfoBlock"
              class="pr-3 mt-4"
            >
              <h6 class="hot-edit-label">Additional Information</h6>
              <b-link
                :href="location.osm_url"
                target="_blank"
                class="d-block mb-2"
              >
                View Map
                <b-icon
                  icon="box-arrow-up-right"
                  variant="secondary"
                  class="ml-2"
                ></b-icon>
              </b-link>
              <div class="mb-2">
                <span
                  v-if="location.phone_number"
                  class="hot-edit-label mr-2"
                  >Tel</span
                >
                <phone-number-display
                  :number="location.phone_number"
                  :extension="location.phone_extension"
                  with-link
                ></phone-number-display>
              </div>
            </div>
          </div>
        </b-card-text>
      </b-card-body>
      <b-card-img
        v-if="!hideMap && staticMapImgSrc"
        :src="hideMap ? '' : staticMapImgSrc"
        :alt="location.name"
        right
        :height="200"
        :width="300"
        referrerpolicy="strict-origin"
      ></b-card-img>
    </b-card>
  </div>
</template>

<script>
  import { suppliosApi } from '../js/api-service';
  import TimeZoneClock from './nuggets/TimeZoneClock';
  import { currentUserInfoMixin } from '../js/custom-vue-mixins';
  import { stadiaMapStaticImgSrc } from '../js/filters-helpers';

  export default {
    name: '',
    components: {
      TimeZoneClock,
    },
    mixins: [currentUserInfoMixin],
    props: {
      location: {
        // the full object directly from API
        type: Object,
        required: true,
      },
      hideMap: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    data: function () {
      return {
        errorMsg: 'Error',
        error: false,
        loadingDelete: false, // when we're doing the call to delete it
      };
    },
    computed: {
      showAddressBlock() {
        // should we show the address block at all?
        const parts = [
          this.location.user_addr1,
          this.location.user_addr2,
          this.location.formatted_city_state,
          this.location.user_postcode,
          this.location.api_postcode,
          this.location.country_name,
        ];
        return parts.some((part) => !!part); // do we have any of the parts?
      },
      showLocalTimeBlock() {
        // should we show the 'Current Local Time' block at all?
        const parts = [this.location.time_zone]; // add more if you add more things to the block
        return parts.some((part) => !!part);
      },
      showAdditionalInfoBlock() {
        // should we show the "Additional Information" block at all?
        const parts = [this.location.osm_url, this.location.phone_number]; // add more if you add more things to the block
        return parts.some((part) => !!part);
      },
      staticMapImgSrc() {
        return stadiaMapStaticImgSrc({
          style: 'osm_bright',
          lat: this.location.lat,
          lng: this.location.lng,
          height: 200,
          width: 300,
          zoom: 6,
        });
      },
      isDisabled() {
        return this.disabled || this.loadingDelete;
      },
    },
    methods: {
      handleError: function (error, message) {
        console.log(message);
        console.log(error);
        this.errorMsg = message;
        this.error = true;
      },
      joinElements: function (arrayToJoin) {
        // take an array of strings, some may be empty strings
        // filter out the empty ones
        let withValues = arrayToJoin.filter((elem) => !!elem);
        // then join that with comma-separated
        return withValues.join(', ');
      },
      handleLocationEdit: function (locationObject) {
        // emit something to parent saying this company wants edit
        this.$emit('request-edit', locationObject);
        console.log(locationObject);
      },
      handleLocationDelete: function (locationObj) {
        this.$bvModal
          .msgBoxConfirm(`Are you sure you want to delete the address ${locationObj.name}?`, {
            title: 'Please Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes, Delete',
            cancelTitle: 'No, Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          })
          .then((value) => {
            if (value) {
              this.performDelete(locationObj);
            } else {
              // don't do anything... since user just closed modal in this case, or clicked cancel
            }
          })
          .catch((err) => this.handleError(err, 'Error while trying to delete this location'));
      },
      performDelete: function (locationObj) {
        this.loadingDelete = true;
        let locationId = locationObj.id;
        return suppliosApi.companyLocation
          .destroy(locationId)
          .then((response) => {
            this.$emit('deleted', response.data);
            this.$toast.success('Location successfully deleted');
            return true;
          })
          .catch((err) => {
            this.handleError(err, 'Error while deleting this location');
            return false;
          })
          .then((status) => {
            this.loadingDelete = false;
            return status;
          });
      },
      setPrimaryLocation: function (locationObj) {
        // handle click on 'Set Primary' button
        // don't do it here (since we don't have company object) but emit to parent and let it handle API call
        this.$emit('request-set-primary', locationObj);
      },
    },
  };
</script>

<style scoped>
  .company-location-card {
    border-left: 4px solid var(--secondary);
  }

  .company-location-card img {
    object-fit: cover;
  }
</style>
